// CustomFieldBlocks.jsx — CONTAINERS dos blocos do CD que vivem no detalhe do agendamento e na ficha da pessoa.
import { useMemo, useState } from 'react';
import AppSnackbar from '../../components/ui/AppSnackbar';
import useSnackbar from '../../hooks/useSnackbar';
import { useAppointmentAnswers, usePersonCustomFields } from '../../hooks/queries/useCustomFields';
import { AppointmentAnswers, PersonCustomFields } from './CustomFieldsView';
import { buildAppointmentAnswersVm, buildPersonFieldsVm, fieldErrorsFrom, toChangedAnswers } from './customFieldsVmBuilder';

const LOAD_ERROR = 'Não foi possível carregar. Tente novamente.';
const messageOf = (error) => error?.response?.data?.message || 'Não foi possível salvar. Tente novamente.';

/** "Usar esta" e "Ignorar". O erro fica na própria divergência: um 409 de CPF precisa aparecer ao lado da decisão. */
function useDivergence(resolve) {
  const [resolving, setResolving] = useState(null);

  const run = async (valueId, action) => {
    setResolving({ id: valueId, error: null });
    try {
      await resolve({ valueId, action });
      setResolving(null);
    } catch (error) {
      setResolving({ id: valueId, error: messageOf(error) });
    }
  };

  return {
    resolvingId: resolving?.id ?? null,
    resolveError: resolving?.error ?? null,
    onApply: (valueId) => run(valueId, 'apply'),
    onIgnore: (valueId) => run(valueId, 'ignore'),
  };
}

export function AppointmentAnswersBlock({ appointmentId }) {
  const { data, loading, error, reload, save, saving, resolve } = useAppointmentAnswers(appointmentId);
  const { resolvingId, resolveError, onApply, onIgnore } = useDivergence(resolve);
  const { snackbar, showError, hideSnackbar } = useSnackbar();
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  const vm = useMemo(() => buildAppointmentAnswersVm({
    loading, errorMessage: error ? LOAD_ERROR : null, data, values, errors, saving, resolvingId, resolveError,
  }), [loading, error, data, values, errors, saving, resolvingId, resolveError]);

  const handleSave = async () => {
    const fields = [
      ...(data?.answers || []).map((answer) => ({ id: answer.customFieldId, fieldType: answer.fieldType })),
      ...(data?.unanswered || []),
    ];
    try {
      await save(toChangedAnswers(values, fields));
      setValues({});
      setErrors({});
    } catch (saveError) {
      const fieldErrors = fieldErrorsFrom(saveError);
      if (fieldErrors) setErrors(fieldErrors);
      else showError(messageOf(saveError));
    }
  };

  const actions = {
    onFieldChange: (fieldId, value) => {
      setValues((current) => ({ ...current, [fieldId]: value }));
      setErrors((current) => ({ ...current, [fieldId]: null }));
    },
    onSave: handleSave,
    onApply,
    onIgnore,
    onRetry: () => reload(),
  };

  return (
    <>
      <AppointmentAnswers vm={vm} actions={actions} />
      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </>
  );
}

export function PersonCustomFieldsBlock({ personId }) {
  const { data, loading, error, reload, resolve } = usePersonCustomFields(personId);
  const { resolvingId, resolveError, onApply, onIgnore } = useDivergence(resolve);

  const vm = useMemo(() => buildPersonFieldsVm({
    loading, errorMessage: error ? LOAD_ERROR : null, data, resolvingId, resolveError,
  }), [loading, error, data, resolvingId, resolveError]);

  return <PersonCustomFields vm={vm} actions={{ onApply, onIgnore, onRetry: () => reload() }} />;
}
